import { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Stamp, Type, X } from 'lucide-react';
import { useEditor } from '../../state/store';
import { Color, TRANSPARENT } from '../../core/types';
import { sampleGradient } from '../../tools/gradient';
import {
  BitMask,
  FILL_LABELS,
  FillStyle,
  MODE_LABELS,
  RenderMode,
  SUGGESTED_ROWS,
  dotsPerCell,
  generateFromMask,
  makeMask,
} from '../../tools/textGen';
import {
  DEFAULT_FONT,
  DEFAULT_IMAGE_MASK,
  FONT_CHOICES,
  FontSpec,
  ImageMaskOptions,
  SCRIPT_FONT,
  SCRIPT_ITALIC_FONT,
  STROKE_FONT,
  isBuiltInFont,
  rasterizeImage,
  rasterizeText,
} from '../../tools/textRaster';
import { renderStrokeText } from '../../tools/strokeFont';
import { renderScriptText } from '../../tools/scriptFont';
import { SourceImage, loadImageData } from '../../image/convert';
import { CellWrite } from '../../tools/types';
import { pickFile } from '../fileIO';
import { Checkbox, Field, Hint, NumberInput, Panel } from '../widgets/common';
import { Select } from '../widgets/primitives';

const FILL_STYLES: FillStyle[] = [
  'braille',
  'quarter',
  'halfblock',
  'block',
  'shade',
  'box-double',
  'box-heavy',
  'box-light',
  'custom',
];

const MODES: RenderMode[] = ['solid', 'knockout', 'outline'];

/**
 * Text and logo generator.
 *
 * Write a word, pick a font and a character family, and the letterforms are
 * rebuilt out of those characters — or cut out of a solid slab of them, which
 * is what "knockout" does. The same pipeline takes an image, so a logo can be
 * punched through a Braille block exactly like text.
 */
export function TextGenPanel(): JSX.Element {
  const brush = useEditor((s) => s.brush);
  // Subscribed so the destination shown on the Insert button follows the
  // pointer, the caret and the selection as they change.
  useEditor((s) => s.cursor);
  useEditor((s) => s.caret);
  const revision = useEditor((s) => s.revision);
  const insertAt = useEditor.getState().insertOrigin();
  const selectionBounds = useMemo(
    () => useEditor.getState().active().selection.getBounds(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revision],
  );

  const [source, setSource] = useState<'text' | 'image'>('text');
  const [text, setText] = useState('PIXANSI');
  const [font, setFont] = useState<FontSpec>(DEFAULT_FONT);
  const [mode, setMode] = useState<RenderMode>('knockout');
  const [fill, setFill] = useState<FillStyle>('braille');
  const [customChar, setCustomChar] = useState('█');
  const [padX, setPadX] = useState(2);
  const [padY, setPadY] = useState(1);
  const [outlineThickness, setOutlineThickness] = useState(1);
  const [useGradient, setUseGradient] = useState(true);
  const [paintBackground, setPaintBackground] = useState(false);

  const [image, setImage] = useState<SourceImage | null>(null);
  const [imageName, setImageName] = useState('');
  const [imageOptions, setImageOptions] = useState<ImageMaskOptions>(DEFAULT_IMAGE_MASK);

  const previewRef = useRef<HTMLPreElement>(null);

  const target = useMemo(() => {
    const dots = dotsPerCell(fill);
    return { dotsX: dots.x, dotsY: dots.y, cellAspect: 2 };
  }, [fill]);

  /** Vertical detail available to the letterforms. */
  const dotResolution =
    (source === 'image' ? imageOptions.rows : font.rows) * dotsPerCell(fill).y;

  const usesStrokeFont = source === 'text' && isBuiltInFont(font.family);

  /** The shape, rasterised at the fill style's resolution. */
  const shape: BitMask = useMemo(() => {
    if (source === 'image') {
      return image ? rasterizeImage(image, imageOptions, target) : makeMask(0, 0);
    }
    const spacing = Math.max(0, Math.round(font.letterSpacing));
    // The built-in alphabets are one dot per cell by definition, so the fill's
    // sub-cell resolution is not involved — the alphabet already is the cell
    // layout. Each is drawn by its own renderer rather than by the browser.
    if (font.family === STROKE_FONT) {
      return renderStrokeText(text, { letterSpacing: spacing });
    }
    if (font.family === SCRIPT_FONT || font.family === SCRIPT_ITALIC_FONT) {
      return renderScriptText(text, {
        // The script glyphs carry ink at both edges so they can join, which
        // means butting them together merges neighbouring letters into one
        // shape. One column apart is the tightest legible join, and the
        // renderer bridges the gap so the hand stays continuous.
        letterSpacing: Math.max(1, spacing),
        slant: font.family === SCRIPT_ITALIC_FONT,
      });
    }
    return rasterizeText(text, font, target);
  }, [source, text, font, target, image, imageOptions]);

  const result = useMemo(() => {
    const gradient = brush.gradient;
    return generateFromMask(shape, {
      mode,
      fill,
      customChar,
      padX,
      padY,
      outlineThickness,
      attrs: brush.attrs,
      colorAt: (x, y, cols, rows) => {
        if (!useGradient) {
          return { fg: brush.fg, bg: paintBackground ? brush.bg : TRANSPARENT };
        }
        const color: Color = sampleGradient(
          gradient,
          x,
          y,
          { x: 0, y: 0 },
          { x: Math.max(1, cols - 1), y: Math.max(1, rows - 1) },
        );
        return { fg: color, bg: paintBackground ? brush.bg : TRANSPARENT };
      },
    });
  }, [shape, mode, fill, customChar, padX, padY, outlineThickness, useGradient, paintBackground, brush]);

  // Coloured preview, same treatment as the image importer.
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    el.textContent = '';
    if (result.inkCells === 0) return;
    const grid = result.grid;
    for (let y = 0; y < grid.height; y++) {
      const line = document.createElement('div');
      let run: HTMLSpanElement | null = null;
      let runFg = -2;
      for (let x = 0; x < grid.width; x++) {
        const cell = grid.get(x, y);
        if (!run || cell.fg !== runFg) {
          run = document.createElement('span');
          runFg = cell.fg;
          run.style.color = cell.fg === TRANSPARENT ? '#c8ccd4' : `#${cell.fg.toString(16).padStart(6, '0')}`;
          line.appendChild(run);
        }
        run.textContent += cell.char === '' ? ' ' : cell.char;
      }
      el.appendChild(line);
    }
  }, [result]);

  const insert = () => {
    const store = useEditor.getState();
    if (result.inkCells === 0) {
      store.notify('Nothing to insert — the shape came out empty.', 'warn');
      return;
    }
    const origin = store.insertOrigin();
    const writes: CellWrite[] = [];
    for (let y = 0; y < result.grid.height; y++) {
      for (let x = 0; x < result.grid.width; x++) {
        const cell = result.grid.get(x, y);
        if (cell.char === '' && cell.bg === TRANSPARENT) continue;
        writes.push({ x: origin.x + x, y: origin.y + y, cell });
      }
    }
    store.writeCells(writes, source === 'image' ? 'Insert logo' : 'Insert generated text');

    // If it was aimed at a selection, say whether it fitted — the lettering is
    // sized by row count, so it can easily be wider than the box drawn for it.
    const bounds = store.active().selection.getBounds();
    const overflows =
      bounds && (result.cols > bounds.width || result.rows > bounds.height)
        ? ` — larger than the ${bounds.width}×${bounds.height} selection`
        : '';
    store.notify(
      `Inserted ${result.cols}×${result.rows} cells at ${origin.x}, ${origin.y}${overflows}.`,
      overflows ? 'warn' : 'success',
    );
  };

  const chooseImage = async () => {
    const file = await pickFile('image/png,image/jpeg,image/webp,image/gif,image/bmp,image/svg+xml');
    if (!file) return;
    try {
      setImage(await loadImageData(file));
      setImageName(file.name);
    } catch (e) {
      useEditor.getState().notify(`Could not read the image: ${(e as Error).message}`, 'error');
    }
  };

  return (
    <>
      <Panel title="Generator" collapsible={false}>
        <div className="segmented" style={{ marginBottom: 8 }}>
          <button className={source === 'text' ? 'active' : ''} onClick={() => setSource('text')}>
            <Type size={13} /> Text
          </button>
          <button className={source === 'image' ? 'active' : ''} onClick={() => setSource('image')}>
            <ImagePlus size={13} /> Logo
          </button>
        </div>

        {source === 'text' ? (
          <>
            <textarea
              className="input"
              style={{ height: 54 }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Text to generate"
            />
            <Field label="Font">
              <Select
                ariaLabel="Font"
                value={font.family}
                options={FONT_CHOICES.map((f) => ({
                  value: f.value,
                  label: f.label,
                  hint: f.note,
                  group: f.group,
                }))}
                onChange={(family) => {
                  setFont({ ...font, family });
                  // The stroke alphabet is drawn in box characters; anything
                  // else would throw away the one-cell strokes it is made of.
                  if (isBuiltInFont(family) && !fill.startsWith('box-')) setFill('box-double');
                }}
              />
            </Field>
            {usesStrokeFont ? (
              <Hint>
                {font.family === SCRIPT_FONT || font.family === SCRIPT_ITALIC_FONT
                  ? 'A joined hand: each letter leaves a stroke on the baseline that the next one picks up, so the word runs continuously. Set spacing to 0 for the tightest join.'
                  : 'Seven rows tall with one-cell strokes — narrow lettering drawn straight in box characters.'}{' '}
                These alphabets are drawn on the grid itself, so there is no size or weight to set.
              </Hint>
            ) : (
              <Field label="Height">
                <div className="row">
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={font.rows}
                    style={{ flex: 1 }}
                    onChange={(e) => setFont({ ...font, rows: Number(e.target.value) })}
                  />
                  <span className="mono dim" style={{ width: 40, textAlign: 'right' }}>
                    {font.rows} r
                  </span>
                </div>
              </Field>
            )}
            <Field label="Spacing">
              <NumberInput
                value={font.letterSpacing}
                min={-8}
                max={16}
                onChange={(v) => setFont({ ...font, letterSpacing: v })}
                title="Extra space between letters"
              />
            </Field>
            {usesStrokeFont ? null : (
              <div className="row">
                <Checkbox label="Bold" checked={font.bold} onChange={(v) => setFont({ ...font, bold: v })} />
                <Checkbox
                  label="Italic"
                  checked={font.italic}
                  onChange={(v) => setFont({ ...font, italic: v })}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <button className="btn solid tall" style={{ width: '100%' }} onClick={chooseImage}>
              <ImagePlus size={15} />
              {image ? `${imageName} (${image.width}×${image.height})` : 'Choose a logo…'}
            </button>
            {image ? (
              <button
                className="btn"
                style={{ width: '100%', marginTop: 4 }}
                onClick={() => {
                  setImage(null);
                  setImageName('');
                }}
              >
                <X size={13} /> Clear
              </button>
            ) : null}
            <Field label="Height">
              <div className="row">
                <input
                  type="range"
                  min={1}
                  max={40}
                  value={imageOptions.rows}
                  style={{ flex: 1 }}
                  onChange={(e) => setImageOptions({ ...imageOptions, rows: Number(e.target.value) })}
                />
                <span className="mono dim" style={{ width: 40, textAlign: 'right' }}>
                  {imageOptions.rows} r
                </span>
              </div>
            </Field>
            <Field label="Threshold">
              <input
                type="range"
                min={0}
                max={255}
                value={imageOptions.threshold}
                style={{ width: '100%' }}
                onChange={(e) => setImageOptions({ ...imageOptions, threshold: Number(e.target.value) })}
              />
            </Field>
            <Checkbox
              label="Use dark pixels as the shape"
              checked={imageOptions.invert}
              onChange={(v) => setImageOptions({ ...imageOptions, invert: v })}
              title="For a logo drawn dark on a light background"
            />
          </>
        )}
      </Panel>

      <Panel title="Built from" collapsible={false}>
        <Field label="Characters">
          <Select
            ariaLabel="Characters"
            value={fill}
            options={FILL_STYLES.map((f) => ({ value: f, label: FILL_LABELS[f] }))}
            onChange={(next) => {
              setFill(next);
              /*
               * Re-scale the height for the new family. Keeping the row count
               * would silently change how legible the text is, because each
               * family carries a different amount of detail per row.
               */
              setFont((f) => ({ ...f, rows: SUGGESTED_ROWS[next] }));
              setImageOptions((o) => ({ ...o, rows: SUGGESTED_ROWS[next] * 2 }));
            }}
          />
        </Field>
        {fill === 'custom' ? (
          <Field label="Character">
            <input
              className="input mono"
              style={{ textAlign: 'center' }}
              value={customChar}
              onChange={(e) => setCustomChar([...e.target.value].pop() ?? '█')}
            />
          </Field>
        ) : null}

        <div className="group" style={{ gap: 5, marginTop: 4 }}>
          {MODES.map((m) => (
            <button
              key={m}
              className={`option${mode === m ? ' active' : ''}`}
              onClick={() => setMode(m)}
              title={MODE_LABELS[m]}
            >
              <span className="label">{MODE_LABELS[m]}</span>
            </button>
          ))}
        </div>

        <div className="row" style={{ marginTop: 8 }}>
          <Field label="Margin X">
            <NumberInput value={padX} min={0} max={20} onChange={setPadX} />
          </Field>
        </div>
        <Field label="Margin Y">
          <NumberInput value={padY} min={0} max={20} onChange={setPadY} />
        </Field>
        {mode === 'outline' ? (
          <Field label="Thickness">
            <NumberInput value={outlineThickness} min={1} max={6} onChange={setOutlineThickness} />
          </Field>
        ) : null}
        {mode === 'knockout' ? (
          <Hint>The margin is the slab around the text — with none, there is nothing to cut into.</Hint>
        ) : null}
        {fill.startsWith('box-') ? (
          <Hint>
            Box drawing puts one stroke in a whole cell, so letters need height. <b>Outline</b> traces
            the letterforms as lines; <b>Solid</b> fills them in with ╬.
          </Hint>
        ) : null}
        {dotResolution < 8 && !usesStrokeFont ? (
          <Hint>
            <span className="warn-text">⚠ Only {dotResolution} dots tall</span> — raise the height or
            pick a finer character family, or the letters will not be readable.
          </Hint>
        ) : null}
      </Panel>

      <Panel title="Colour" collapsible={false}>
        <Checkbox
          label="Use the gradient"
          checked={useGradient}
          onChange={setUseGradient}
          title="Edit the ramp in the Colour section"
        />
        <Checkbox
          label="Paint cell backgrounds"
          checked={paintBackground}
          onChange={setPaintBackground}
        />
        <Hint>
          {useGradient
            ? 'The ramp runs corner to corner across the generated block.'
            : 'Uses the current text colour.'}
        </Hint>
      </Panel>

      <Panel title="Preview" collapsible={false}>
        <pre
          ref={previewRef}
          className="terminal-preview"
          style={{ maxHeight: 190, fontSize: 9, lineHeight: 1.05 }}
        />
        <div className="row" style={{ marginTop: 6 }}>
          <span className="faint mono">
            {result.inkCells > 0 ? `${result.cols}×${result.rows} cells` : 'empty'}
          </span>
        </div>
        <button
          className="btn primary tall"
          style={{ width: '100%', marginTop: 8 }}
          disabled={result.inkCells === 0}
          onClick={insert}
        >
          <Stamp size={15} />
          Insert at {insertAt.x}, {insertAt.y}
        </button>
        <Hint>
          {selectionBounds
            ? `Goes into the selection, starting at its top-left corner. The shape is ${result.cols}×${result.rows} and the selection is ${selectionBounds.width}×${selectionBounds.height}.`
            : 'Select an area first to place it there, or it goes to the text caret — failing that, wherever the pointer last was on the canvas.'}
        </Hint>
      </Panel>
    </>
  );
}
